import React, { useEffect, useState } from "react";
import {
  fetchActivities,
  fetchManagement,
  fetchRevenue,
  fetchSummary,
} from "./data/dashboardApi";

import "bootstrap/dist/css/bootstrap.min.css";
import "./revenue.css";

// CARD WRAPPER
const Card = ({ children }) => (
  <div className="dashboard-card mb-4">{children}</div>
);

const SummaryCards = ({ data }) => {
  const safeRevenue = Number(data?.totalRevenue) || 0;
  const safeOrders = Number(data?.totalOrders) || 0;
  const safeGrowth = Number(data?.growthPercent) || 0;

  return (
    <div className="row g-3 mb-4">
      <div className="col-md-4">
        <Card>
          <p className="label">Total Revenue</p>
          <h2 className="value">₹{safeRevenue.toLocaleString()}</h2>
          <span className="growth">+{safeGrowth}% Growth</span>
        </Card>
      </div>
      <div className="col-md-4">
        <Card>
          <p className="label">Total Orders</p>
          <h2 className="value">{safeOrders.toLocaleString()}</h2>
        </Card>
      </div>
      <div className="col-md-4">
        <Card>
          <p className="label">Conversion Rate</p>
          <h2 className="value">{Math.round(safeGrowth * 1.8)}%</h2>
        </Card>
      </div>
    </div>
  );
};

const ActivitiesCard = ({ activities }) => (
  <Card>
    <h5 className="section-title mb-4">Monthly Performance</h5>

    <div className="bar-container">
      {activities?.months?.map((m, i) => (
        <div key={i} className="bar-wrapper">
          <div
            className="bar"
            style={{ height: `${activities.values[i] * 1.8}px` }}
          ></div>
          <span className="bar-label">{m}</span>
        </div>
      ))}
    </div>
  </Card>
);

const TotalRevenueCard = ({ revenue }) => (
  <Card>
    <h5 className="section-title mb-4">Total Revenue</h5>
    <h2 className="value mb-4">{revenue?.totalLabel}</h2>

    <div className="heatmap">
      {revenue?.heatmap?.flat()?.map((level, i) => (
        <div key={i} className={`heat-cell heat-${level}`}></div>
      ))}
    </div>
  </Card>
);

const ManagementCard = ({ management }) => (
  <Card>
    <h5 className="section-title mb-3">Latest Transactions</h5>

    <div className="table-responsive">
      <table className="table table-borderless align-middle">
        <thead>
          <tr className="table-header">
            <th>Order ID</th>
            <th>Date</th>
            <th>Service Type</th>
            <th>Customer</th>
            <th>Price</th>
            <th>Payment Mode</th>
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          {management?.orders?.map((o) => (
            <tr key={o.id} className="table-row">
              <td>{o.id}</td>
              <td>{o.date}</td>
              <td>
                {o.serviceType.charAt(0).toUpperCase() + o.serviceType.slice(1)}
              </td>
              <td>{o.customer}</td>
              <td>{o.price}</td>
              <td>{o.paymentStatus}</td>
              <td>{o.status === "Settled" ? "Paid" : "Unpaid"}</td>
            </tr>
          ))}

          {(!management?.orders || management.orders.length === 0) && (
            <tr>
              <td colSpan="6" className="text-center text-muted">
                No Transactions Found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </Card>
);

const Revenue = () => {
  const [data, setData] = useState({
    summary: null,
    activities: null,
    revenue: null,
    management: null,
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [activities, summary, revenue, management] = await Promise.all([
          fetchActivities(),
          fetchSummary(),
          fetchRevenue(),
          fetchManagement(),
        ]);

        setData({
          summary,
          activities,
          revenue,
          management,
        });
      } catch (error) {
        console.error("API Error:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center w-100 py-5">
        <div className="spinner-border text-success" role="status"></div>
      </div>
    );

  return (
    <div className="container py-4">
      <SummaryCards data={data.summary} />

      <div className="row g-4 mb-4">
        <div className="col-lg-6">
          <ActivitiesCard activities={data.activities} />
        </div>
        <div className="col-lg-6">
          <TotalRevenueCard revenue={data.revenue} />
        </div>
      </div>

      <ManagementCard management={data.management} />
    </div>
  );
};

export default Revenue;
